/**
 * Session-recorder client-side staff-eligibility helpers.
 *
 * Two concerns live here because they're the two client-side companions
 * to the server's `App\Support\StaffEligibility::validateChain` predicate:
 *
 *   1. `filterEligibleForRecorder` — client-side hard-filter matching the
 *      recorder's policy (drops rows the server would reject). Same
 *      semantics as StaffEligibility::validateChain, applied to rows
 *      the assign endpoint returned via its soft-annotate policy.
 *
 *   2. `classifyBlockingMessage` — regex-lite classifier that maps an
 *      autosave rejection message to the wizard step whose field owns
 *      it. Surfaces the message as an inline Alert at the offending
 *      picker instead of the (removed) full-page block card.
 *
 * If you're tempted to move either function's LOGIC to a call site,
 * don't. The filter had duplication with the picker's inline post-filter
 * until this file was extracted (T48-shape defect); the classifier is
 * tested via `staffEligibility.test.ts` and inline copies would defeat
 * the test.
 */

/**
 * Client-side hard-filter for the recorder's staff picker and the two
 * auto-fill paths (URL-param preselect + single-assigned auto-select).
 *
 * The assign endpoint (`/aba/staff/search`) returns location-mismatched
 * and PSA-mismatched staff as annotated rows (soft policy — admin can
 * assign across locations). The recorder needs the hard policy: those
 * rows never enter the picker or the auto-fill path.
 *
 * Two flags cover the two gates the server-side `validateChain` would
 * otherwise apply at autosave time:
 *   - `location_mismatch === true` → wrong location (check 5)
 *   - `psa_match === false` → program has PSAs but this staff isn't
 *     on one (check 6). Tri-state: `null` means "no PSA-gate applies"
 *     which reads as pass via `!== false`.
 *
 * The other four `validateChain` checks (deleted / no-user / inactive
 * / suspended) are already hard-filtered by the assign endpoint's own
 * exclusion at [ProgramAssignmentController.php:316-319], so no
 * additional client-side check is needed for them.
 */
export function filterEligibleForRecorder<T extends {
  location_mismatch?: boolean;
  psa_match?: boolean | null;
}>(rows: T[]): T[] {
  return rows.filter((r) => r.location_mismatch !== true && r.psa_match !== false);
}

/**
 * Map an autosave rejection `message` to the wizard step whose picker
 * owns the field. Return `null` for unclassified messages so the caller
 * can fall back to a page-level Alert.
 *
 * The message strings this classifier matches against are wire-format
 * strings that live server-side. As of writing, they're INLINE literals
 * in three files (no shared constants file exists), so a message rewrite
 * on the server would not automatically break TypeScript here — a
 * rejection would just fail to classify and the page-level fallback
 * would fire. `staffEligibility.test.ts` locks in the current strings
 * as assertions; if a server-side rewrite lands, that test breaks and
 * points at the pattern that stopped matching.
 *
 * Source-of-truth locations (do NOT edit either side alone):
 *   - Staff variants: `backend/app/Support/StaffEligibility.php`
 *     (`validateChain`, six literal returns).
 *   - "Selected Program is not assigned to this Student.":
 *     `backend/app/Http/Controllers/SessionDataController.php:704`.
 *   - "Selected Target is not part of this Program.":
 *     `backend/app/Http/Controllers/SessionDataController.php:710`.
 *   - "Program is locked. Unlock it before...":
 *     multiple controllers (see `ProgramLockGuard::MESSAGE` and
 *     `ProgramController::update / destroy / updateStatus`).
 */
export function classifyBlockingMessage(message: string | null | undefined): number | null {
  const m = (message ?? "").toLowerCase();
  if (!m) return null;
  // Lock-related messages ALWAYS route to Program regardless of what
  // else they mention. ProgramLockGuard's message reads "Program is
  // locked. Unlock it before editing its targets." — the word "targets"
  // is content, not a routing hint. The user can only fix it by
  // unlocking the program, which is a Program-step action.
  if (m.includes("locked") || m.includes("unlock")) return 2;
  if (m.includes("staff")) return 5;
  if (m.includes("target")) return 3;
  if (m.includes("program")) return 2;
  return null;
}
